import { useState, useEffect, useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import ProjectService from '@/shared/services/ProjectService';
import type { Project } from '@/shared/types';
import { ROUTES } from '@/shared/services/constants';
import { Loading, ProjectPopup } from '@/shared/components/ui';
import { logger } from '@/shared/utils';
import { useSafeThemeClasses } from '@/shared/theme';
import { data } from '@/content/data';
import { normalizeMediaPlaybackRate, preloadMediaScope, useMedia } from '@/resources/media';
import { SEO } from '@/shared/components/SEO';
import { ENV } from '@/config/environment';

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

type HeroLineTone = 'default' | 'muted' | 'secondary' | 'accent' | 'hero';

type HeroLineConfig = {
  text: string;
  tone?: HeroLineTone;
};

function renderHeroLineWithEmphasis(
  line: string,
  strikeWords: string[],
  highlightPhrases: string[],
  strikeClass: string,
  highlightClass: string
): ReactNode {
  const patterns = [
    ...strikeWords.filter(Boolean),
    ...highlightPhrases.filter(Boolean),
  ];
  if (!patterns.length) return line;

  const pattern = patterns.map(escapeRegExp).join('|');
  if (!pattern) return line;

  const re = new RegExp(`(${pattern})`, 'gi');
  const parts = line.split(re);
  const strikeLower = new Set(strikeWords.map((w) => w.toLowerCase()));
  const highlightLower = new Set(highlightPhrases.map((w) => w.toLowerCase()));

  return parts.map((part, i) => {
    if (!part) return null;
    const lower = part.toLowerCase();
    if (strikeLower.has(lower)) {
      return (
        <span key={`s-${i}-${part}`} className={strikeClass}>
          {part}
        </span>
      );
    }
    if (highlightLower.has(lower)) {
      return (
        <span key={`h-${i}-${part}`} className={highlightClass}>
          {part}
        </span>
      );
    }
    return part;
  });
}

function heroToneClasses(tone: HeroLineTone | undefined, theme: ReturnType<typeof useSafeThemeClasses>): string {
  switch (tone) {
    case 'muted':
      return theme.textMuted;
    case 'secondary':
      return theme.textSecondary;
    case 'accent':
      return theme.textAccent;
    case 'hero':
      return `${theme.textAccent} drop-shadow-sm`;
    default:
      return theme.textHeading;
  }
}

function heroSizeClasses(tone: HeroLineTone | undefined): string {
  if (tone === 'hero') {
    return 'text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight';
  }
  if (tone === 'muted') {
    return 'text-2xl sm:text-3xl md:text-4xl font-medium';
  }
  return 'text-3xl md:text-5xl';
}

const HOME_MEDIA_SCOPE = 'welcome';
const HOME_BG_VIDEO = 'hero.backgroundVideo';

export default function WelcomePage() {
  const theme = useSafeThemeClasses();
  const { media: homeBgMedia } = useMedia(HOME_MEDIA_SCOPE, HOME_BG_VIDEO);
  const [featuredProjects, setFeaturedProjects] = useState<Project[]>([]);
  const [popupProject, setPopupProject] = useState<Project | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const welcome = data.welcome as {
    heroLines?: HeroLineConfig[];
    heroLine?: string;
    heroHighlightPhrases?: string[];
    heroStrikeWords?: string[];
    scrollHint: string;
    featuredProjectsTitle: string;
    loadingFeatured: string;
    featuredError: string;
    tryAgain: string;
    viewAllProjects: string;
  };

  const conversationLines: HeroLineConfig[] =
    welcome.heroLines?.length
      ? welcome.heroLines
      : (welcome.heroLine ?? '').split('\n').map((text) => ({ text: text.trim(), tone: 'default' as const }));

  const heroStrikeWords: string[] = welcome.heroStrikeWords ?? [];
  const heroHighlightPhrases: string[] = welcome.heroHighlightPhrases ?? [];
  const heroVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    async function fetchFeaturedProjects() {
      try {
        const projects = await ProjectService.getFeaturedProjects();
        setFeaturedProjects(projects.slice(0, 3));
        setHasError(false);
        logger.info('Featured projects loaded successfully', 'APP');
      } catch (error) {
        logger.error('Failed to fetch featured projects', 'WelcomePage', { error });
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    }

    fetchFeaturedProjects();

    void preloadMediaScope(HOME_MEDIA_SCOPE);
    
    // Animation delay
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const el = heroVideoRef.current;
    if (!el || homeBgMedia?.kind !== 'video') return;
    el.playbackRate = normalizeMediaPlaybackRate(homeBgMedia.entry.speed);
  }, [homeBgMedia]);

  // Handle opening project popup
  const handleProjectClick = (project: Project) => {
    setPopupProject(project);
    setIsPopupOpen(true);
  };

  // Handle closing project popup
  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setTimeout(() => {
      setPopupProject(null);
    }, 200); // Wait for animation to complete
  };

  const showOrbFallback = !homeBgMedia || homeBgMedia.kind !== 'video';

  return (
    <div className="relative min-h-screen pt-[4.8rem] pb-16">
      <SEO
        title={ENV.app.name}
        path="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: ENV.app.name,
          url: ENV.app.siteUrl,
          description: ENV.app.description,
        }}
      />
      <div className={theme.backgroundFixed} aria-hidden />
      {homeBgMedia?.kind === 'video' ? (
        <div className={theme.mediaHeroBackdrop} aria-hidden>
          <video
            ref={heroVideoRef}
            className={theme.mediaHeroBackdropVideo}
            src={homeBgMedia.url}
            poster={homeBgMedia.thumbnailUrl}
            muted
            loop
            playsInline
            autoPlay
            preload="metadata"
            onLoadedMetadata={(e) => {
              e.currentTarget.playbackRate = normalizeMediaPlaybackRate(homeBgMedia.entry.speed);
            }}
          />
          <div className={theme.mediaHeroBackdropScrim} />
        </div>
      ) : null}

      {showOrbFallback ? (
        <div className={theme.mediaHeroOrbLayer}>
          <div className={`absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl ${theme.orbs[0] || ''}`} />
          <div className={`absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl ${theme.orbs[1] || ''}`} />
        </div>
      ) : null}

      <div className={`max-w-5xl mx-auto px-4 sm:px-6 ${theme.mediaHeroContentLayer} transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        {/* Hero */}
        <section className="mb-20 min-h-[calc(100vh-4.8rem)] flex flex-col items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3 sm:gap-4 text-center px-6">
            {conversationLines.map((lineConfig, i) => {
              const line = lineConfig.text?.trim() ?? '';
              const tone = lineConfig.tone ?? 'default';
              const toneClass = heroToneClasses(tone, theme);
              const sizeClass = heroSizeClasses(tone);
              return (
                <p
                  key={`hero-line-${i}`}
                  className={`${tone === 'hero' ? 'font-extrabold' : 'font-bold'} ${sizeClass} leading-tight ${theme.textMultiline} ${toneClass}`}
                >
                  {renderHeroLineWithEmphasis(
                    line,
                    heroStrikeWords,
                    heroHighlightPhrases,
                    theme.textStrikethrough,
                    `${theme.textAccent} italic`
                  )}
                </p>
              );
            })}
          </div>
          <p className={`mt-10 text-sm ${theme.textSecondary}`}>{welcome.scrollHint}</p>
        </section>
        
        {/* Featured Projects Section */}
        <section className="mb-20">
          <div>
            <h2 className={`text-3xl font-bold mb-10 text-center ${theme.textHeading}`}>{welcome.featuredProjectsTitle}</h2>
            {isLoading ? (
              <div className="py-12 flex justify-center">
                <Loading text={welcome.loadingFeatured} />
              </div>
            ) : hasError ? (
              <div className="py-12 text-center">
                <p className={`text-lg mb-4 ${theme.textSecondary}`}>{welcome.featuredError}</p>
                <div 
                  onClick={() => window.location.reload()} 
                  className={`inline-block py-2 px-4 rounded-lg transition-all duration-300 cursor-pointer text-sm font-medium ${theme.alertError} hover:scale-105`}
                  role="button"
                  tabIndex={0}
                >
                  {welcome.tryAgain}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {featuredProjects.map((project: Project) => (
                  <div 
                    key={project.id} 
                    className="group cursor-pointer"
                    onClick={() => handleProjectClick(project)}
                  >
                    <div className={`rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 h-full flex flex-col ${theme.card}`}>
                      <div className={`aspect-video overflow-hidden relative ${theme.card}`}>
                        {project.image && (
                          <img 
                            src={project.image} 
                            alt={project.title} 
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            style={{ objectPosition: 'center' }}
                          />
                        )}
                      </div>
                      <div className="p-6 flex-grow flex flex-col">
                        <h3 className={`text-xl font-semibold mb-3 group-hover:${theme.textAccent} transition-colors duration-300 ${theme.textHeading}`}>
                          {project.title}
                        </h3>
                        <p className={`mb-4 flex-grow ${theme.textSecondary}`}>
                          {project.description}
                        </p>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {project.tags?.map((tag: string, index: number) => (
                            <span key={`${project.id}-tag-${tag}-${index}`} className={`px-2 py-1 text-xs rounded-full ${theme.badge}`}>{tag}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-10 text-center">
              <Link 
                to={ROUTES.PROJECTS} 
                className={`inline-block py-3 px-6 rounded-xl transition-all duration-300 text-base font-semibold ${theme.buttonSecondary} ${theme.buttonTextSecondary} hover:scale-105 hover:shadow-lg glass-highlight`}
              >
                {welcome.viewAllProjects}
              </Link>
            </div>
          </div>
        </section>
      </div>

      {/* Project Popup */}
      <ProjectPopup
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        project={popupProject}
      />
    </div>
  );
}
